/**
 * EID-126: per-code-version registry identity for warm-recompiled transforms.
 *
 * Consumers dedupe queries by (query hash, source identity) —
 * BaseWorldLayer.cachedQuery — and the event-transform proxy cache
 * (event-transform-proxy-cache.ts) exists precisely because an unchanged
 * identity means a stale cache. A warm recompile (Transform.setCode) keeps
 * the SAME live Transform instance, so after a successful recompile the
 * data-provider must hand consumers a fresh registry identity or every bound
 * consumer keeps serving results from the previous code version.
 *
 * createTransformCodeProxy builds that identity: a lightweight wrapper
 * delegating to the live Transform (mirroring the event-proxy pattern), so
 * the worker stays warm while cachedQuery sees a new source and re-queries.
 * The event-transform proxy cache composes on top unchanged — a new code
 * proxy is a new `tx` identity, so event-using transforms also get a fresh
 * event proxy.
 *
 * NOTE for callers: registering the proxy must NOT go through
 * replaceTransform (transform-registry.ts) — its EID-57 terminate-on-replace
 * contract would terminate the shared live worker the proxy delegates to.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

/**
 * The registry-entry surface consumers touch: cachedQuery calls `query`,
 * layers read `datasource` (coordinate metadata) and `refresh`, the
 * data-provider reads `usesEvent` (event-proxy gating) and the registry
 * helpers call `terminate`.
 */
export interface CodeProxySource {
  query: (q: unknown, squeeze?: boolean) => unknown;
  datasource: unknown;
  refresh: unknown;
  usesEvent: boolean;
  terminate: () => void;
}

export interface TransformCodeProxy {
  query: (q: unknown, squeeze?: boolean) => unknown;
  readonly datasource: unknown;
  readonly refresh: unknown;
  readonly usesEvent: boolean;
  terminate: () => void;
}

/**
 * Wrap the live Transform in a fresh delegating identity. Call once per
 * successful setCode; always wrap the live Transform itself (not a previous
 * proxy) so delegation stays one hop.
 */
export const createTransformCodeProxy = (
  tx: CodeProxySource,
): TransformCodeProxy => ({
  query: (q, squeeze) => tx.query(q, squeeze),
  get datasource() {
    return tx.datasource;
  },
  get refresh() {
    return tx.refresh;
  },
  get usesEvent() {
    return tx.usesEvent;
  },
  terminate: () => tx.terminate(),
});
